import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaEdit, FaTrash } from 'react-icons/fa';
import AddProductModal from '../components/AddProductModal';
import Loader from '../components/Loader';
import ConfirmModal from '../components/ConfirmModal';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import Pagination from '../components/Pagination'; // Add (for next step)


const Mahsulotlar = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [productData, setProductData] = useState({});
  const [saving, setSaving] = useState(false);
  // Product filtering/search state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all'); // 'all' means no filter
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  // Fetch products with owner filtering
  const fetchProducts = async ({ page = currentPage, perPage = itemsPerPage, category = selectedCategory, search = searchTerm } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      // Build query
      const params = new URLSearchParams({
        page: page,
        itemsPerPage: perPage,
      });
      if (category && category !== 'all') params.append('categoryId', category);
      if (search && search.trim() !== '') params.append('search', search.trim());
      const response = await fetch(`/api/products/search?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        }
      });
      
      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }
      
      if (response.status === 403) {
        throw new Error('You don\'t have permission to view products');
      }
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to fetch products');
      }
      
      const data = await response.json();
      setProducts(Array.isArray(data.items) ? data.items : []);
      setTotalItems(data.totalItems || 0);
      setCurrentPage(data.page || 1);
      setItemsPerPage(data.itemsPerPage || perPage);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch categories for filter select
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/categories', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        }
      });
      const data = await response.json();
      setCategories(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setCategories([]);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchProducts({ page: 1 }); // Always go to first page when filter/search changes
  }, []);

  // Fetch on state changes
  useEffect(() => {
    fetchProducts({ page: 1 }); // Always go to first page when filter/search changes
  }, [selectedCategory, searchTerm, itemsPerPage]);
  // Fetch on page change
  useEffect(() => {
    fetchProducts();
  }, [currentPage]);

  // Handlers
  const handleAddProduct = async (productData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        },
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add product');
      }
      setCurrentPage(1); // Go to page 1 after add
      await fetchProducts({ page: 1 });
      toast.success('Product added successfully');
      setShowModal(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleUpdateProduct = async (productData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/products/${editProduct.productid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        },
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update product');
      }
      setCurrentPage(1); // Go to page 1 after update
      await fetchProducts({ page: 1 });
      toast.success('Product updated successfully');
      setShowModal(false);
      setEditProduct(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/products/${deleteId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Delete failed');
      }
      // After delete: refetch for current page, adjust for edge cases
      await fetchProducts();
      toast.success('Mahsulot o\'chirildi');
      setShowConfirm(false);
      setDeleteId(null);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleShowModal = () => {
    setEditProduct(null);
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleEdit = (product) => {
    setEditProduct(product);
    setShowModal(true);
  };

  const handleDeleteConfirm = (id) => {
    setDeleteId(id);
    setShowConfirm(true);
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setDeleteId(null);
  };

  const formatSavingTerm = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString.includes('T') ? dateString : dateString.replace(' ', 'T'));
      if (isNaN(date.getTime())) return 'N/A';
      const pad = num => String(num).padStart(2, '0');
      return `${pad(date.getDate())}-${pad(date.getMonth()+1)}-${date.getFullYear()}`;
    } catch (err) {
      return 'N/A';
    }
  };

  const handleSaveProduct = async () => {
    // Validate required fields
    if (!productData.name || !productData.costing_method) {
      toast.error('Iltimos, mahsulot nomi va hisoblash usulini kiriting!');
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      const url = editProduct 
        ? `/api/products/${editProduct.productid}` 
        : '/api/products';
        
      const method = editProduct ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-owner-id': (user && (user.ownerId || user.id)) ? String(user.ownerId || user.id) : '',
          'x-staff-id': user && user.staffId ? String(user.staffId) : ''
        },
        body: JSON.stringify({
          ...productData,
          // Ensure optional fields are either provided or null
          description: productData.description || null,
          saving_term: productData.saving_term || null,
          photo: productData.photo || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Xatolik yuz berdi');
      }
      
      // Refresh the products list to get complete data
      await fetchProducts();
      
      toast.success(editProduct ? 'Mahsulot muvaffaqiyatli yangilandi!' : 'Mahsulot muvaffaqiyatli qo\'shildi!');
      setShowModal(false);
      setEditProduct(null);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Build absolute image URL from stored photo path
  const getImageUrl = (photo) => {
    if (!photo) return null;
    const p = typeof photo === 'string' ? photo : String(photo);
    return p.startsWith('/') ? p : `/${p}`;
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
      <h2>Mahsulotlar</h2>
      <button className="btn btn-success" onClick={handleShowModal}>Qo'shish</button>
        
      </div>

      <div className="d-flex align-items-center mb-3">
        <select
          className="form-select m-1 w-auto"
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
        >
          <option value="all">Barcha Kategoriyalar</option>
          {categories.map(cat => (
            <option key={cat.id || cat.ID} value={cat.id || cat.ID}>{cat.name}</option>
          ))}
        </select>
        <input
          type="text"
          className="form-control w-25"
          placeholder="Qidiruv (Nom)"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
      </div>
      {loading ? (
        <Loader />
      ) : products.length > 0 ? (
        <>
          <div className="table-responsive rounded-4 shadow">
            <table className="table table-striped table-bordered mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Nom</th>
                <th>Shtrix-kod</th>
                <th>Rasm</th>
                <th>Kategoriya</th>
                <th>Birlik</th>
                <th>Valyuta</th>
                <th>Min. zaxira</th>
                <th>Saqlash muddati</th>
                <th>Hisoblash usuli</th>
                <th>Tavsif</th>
                <th>Amallar</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, idx) => (
                <tr key={p.productid}>
                  <td>{idx + 1}</td>
                  <td>{p.name}</td>
                  <td>{p.barcode || '-'}</td>
                  <td>{p.photo ? <img src={getImageUrl(p.photo)} alt="rasm" style={{ width: 40, height: 40, objectFit: 'cover' }} /> : '-'}</td>
                  <td>{p.category_name}</td>
                  <td>{p.birlik_name}</td>
                  <td>{p.currency || 'UZS'}</td>
                  <td>{p.minimum_quantity || '-'}</td>
                  <td>{formatSavingTerm(p.saving_term)}</td>
                  <td>
                    <span className={`badge ${p.costing_method === 'FIFO' ? 'bg-primary' : 'bg-warning'}`}>
                      {p.costing_method}
                    </span>
                  </td>
                  <td>{p.description || '-'}</td>
                  <td>
                    <button className="btn btn-sm btn-warning me-2" onClick={() => handleEdit(p)} title="Edit">
                      <FaEdit />
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteConfirm(p.productid)} title="Delete">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {products.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={page => setCurrentPage(page)}
              onItemsPerPageChange={n => {
                setItemsPerPage(n);
                setCurrentPage(1); // Reset to first page if itemsPerPage changes
              }}
            />
          )}
        </>
      ) : (
        <div className="alert alert-danger text-center">Ma'lumot topilmadi</div>
      )}
      <AddProductModal
        show={showModal}
        handleClose={handleCloseModal}
        editProduct={editProduct}
        handleAddProduct={handleAddProduct}
        handleUpdateProduct={handleUpdateProduct}
        handleSaveProduct={handleSaveProduct}
        productData={productData}
        setProductData={setProductData}
        saving={saving}
      />
      <ConfirmModal
        show={showConfirm}
        onConfirm={handleDelete}
        onCancel={cancelDelete}
        message="Mahsulotni o'chirishga ishonchingiz komilmi?"
      />
    </div>
  );
};

export default Mahsulotlar;